// src/pages/HelpFAQ.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  HelpOutline as HelpOutlineIcon,
  ContactSupport as ContactSupportIcon,
  Message as MessageIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
} from "@mui/icons-material";

export default function HelpFAQ({ embedded, onBack }) {
  // ✅ Role-agnostic login object (key can still be "dealer" in localStorage)
  const authUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("dealer") || "null");
    } catch {
      return null;
    }
  }, []);

  // ✅ Form state (name/phone are hidden, but used in submit)
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [images, setImages] = useState([]); // ✅ multiple images
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });

  // ✅ Help ticket webhook (Cloudflare Workers)
  const N8N_WEBHOOK =
    "https://sota-help-ticket.sota-help-fipl2.workers.dev/af699120-d154-486c-87aa-6f45e5b0d4ae";

  // ✅ Prefill hidden name + phone from localStorage login details
  useEffect(() => {
    if (!authUser) return;

    const lsName =
      authUser["Contact Person Name"] ||
      authUser.contactPersonName ||
      authUser.name ||
      "";

    const lsPhone =
      String(authUser["Contact No"] ?? "") ||
      String(authUser.phone ?? "") ||
      String(authUser.mobile ?? "") ||
      "";

    setName(lsName);
    setPhone(lsPhone);
  }, [authUser]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    setStatus({ type: "", text: "" });

    setImages(files);

    if (!files.length) {
      setPreviews([]);
      return;
    }

    const readers = files.map(
      (file) =>
        new Promise((resolve) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.readAsDataURL(file);
        })
    );

    const allPreviews = await Promise.all(readers);
    setPreviews(allPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const n = (name || "").trim();
    const p = (phone || "").trim();
    const m = (message || "").trim();

    // ✅ name/phone not visible — but must exist from login
    if (!n || !p) {
      return setStatus({
        type: "error",
        text: "User details not found. Please logout and login again.",
      });
    }

    if (!m)
      return setStatus({ type: "error", text: "Please describe your issue" });

    if (!images.length)
      return setStatus({ type: "error", text: "At least 1 image is required" });

    setSending(true);
    setStatus({ type: "ok", text: "Submitting ticket..." });

    try {
      const fd = new FormData();

      // ✅ same field names as your HTML sample
      fd.append("name", n);
      fd.append("number", p);
      fd.append("remarks", m);

      // ✅ multiple images => append same key
      images.forEach((file) => fd.append("images", file));

      // ✅ optional: send login context (helps support team)
      fd.append("userType", String(authUser?.type || authUser?.Type || ""));
      fd.append("email", String(authUser?.["Email Id"] || authUser?.email || ""));
      fd.append("gst", String(authUser?.["GSTIN/UIN"] || ""));
      fd.append("area", String(authUser?.Area || ""));
      fd.append("district", String(authUser?.District || ""));
      fd.append(
        "address",
        String(authUser?.["Address(As Per GST No. Address)"] || "")
      );
      fd.append("particulars", String(authUser?.Particulars || ""));

      const res = await fetch(N8N_WEBHOOK, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) throw new Error("Server error " + res.status);

      setStatus({ type: "ok", text: "✅ Help ticket submitted successfully!" });

      // ✅ reset (keep hidden name/phone)
      setMessage("");
      setImages([]);
      setPreviews([]);

      const input = document.getElementById("helpImagesInput");
      if (input) input.value = "";
    } catch (err) {
      setStatus({
        type: "error",
        text: "❌ Submission failed: " + (err?.message || "Unknown error"),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="relative bg-gray-900 px-6 sm:px-10 py-6 sm:py-8 text-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/help_bg_image.jpeg"
            alt="Help Center Background"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50" />
        </div>

        <div className="relative z-10">
          {embedded && (
            <button
              onClick={onBack}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white text-4xl sm:text-5xl hover:scale-110 transition-transform duration-200 z-20"
            >
              ×
            </button>
          )}

          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <HelpOutlineIcon
              sx={{ fontSize: 44, color: "#facc15", mb: 1 }}
            />
          </motion.div>

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-2xl sm:text-3xl font-bold text-white font-[DM-sans] mb-1 drop-shadow-2xl"
          >
            Help Center
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-sm sm:text-base text-white max-w-xl sm:max-w-2xl mx-auto drop-shadow-lg"
          >
            Raise a help ticket — our team will handle it quickly.
          </motion.p>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-8 md:h-10 text-black"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C360,100 1080,20 1440,60 L1440,120 L0,120 Z"
              fill="currentColor"
              opacity="0.9"
            />
          </svg>
        </div>
      </div>

      {/* Contact Form */}
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-4 sm:p-6 border-2 border-yellow-300">
          <ContactSupportIcon
            sx={{
              fontSize: 36,
              color: "#ca8a04",
              mb: 1,
              display: "block",
              mx: "auto",
            }}
          />

          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 text-center font-[DM-sans]">
            Still Need Help?
          </h2>

          <p className="text-sm sm:text-base text-gray-700 mb-4 text-center max-w-xl mx-auto">
            Submit details + images. Ticket will be created instantly.
          </p>

          <form
            onSubmit={handleSubmit}
            className="max-w-xl mx-auto space-y-3 sm:space-y-4"
          >
            {/* ✅ Name & Phone are hidden (auto from login), not shown in UI */}

            {/* Message */}
            <div className="relative">
              <MessageIcon className="absolute left-3 sm:left-4 top-2 sm:top-3 text-gray-500" />
              <textarea
                placeholder="Describe your issue... *"
                rows="4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 rounded-xl border border-gray-300 focus:border-yellow-500 focus:outline-none text-base sm:text-lg resize-none"
              />
            </div>

            {/* File Upload (multiple + required) */}
            <div>
              <label className="block text-base sm:text-lg font-medium text-gray-700 mb-2 sm:mb-3 text-center cursor-pointer">
                <div className="flex items-center justify-center gap-3 bg-white rounded-xl border-2 border-dashed border-gray-400 py-4 sm:py-6 hover:border-yellow-500 transition">
                  <AttachFileIcon sx={{ fontSize: 28, color: "#ca8a04" }} />
                  <span>
                    {images.length
                      ? `${images.length} image(s) selected`
                      : "Attach Screenshot*"}
                  </span>
                </div>

                <input
                  id="helpImagesInput"
                  type="file"
                  accept="image/*"
                  multiple
                  required
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>

              {/* Selected list */}
              {images.length > 0 && (
                <div className="text-sm sm:text-base text-gray-700 bg-white rounded-xl border border-gray-200 p-3">
                  <div className="font-semibold mb-2">Selected:</div>
                  <div className="space-y-1">
                    {images.map((f, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="truncate max-w-[70%]">{f.name}</span>
                        <span className="text-gray-500">
                          {Math.round(f.size / 1024)} KB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Previews */}
              {previews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {previews.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Preview ${i + 1}`}
                      className="w-full h-28 sm:h-36 object-cover rounded-xl shadow"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            {status.text && (
              <div
                className={`text-sm sm:text-base font-semibold text-center ${
                  status.type === "error" ? "text-red-600" : "text-green-700"
                }`}
              >
                {status.text}
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={sending}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-base sm:text-xl py-3 sm:py-5 rounded-xl shadow-lg hover:shadow-xl transition flex items-center justify-center gap-3 disabled:opacity-70"
            >
              {sending ? (
                "Submitting..."
              ) : (
                <>
                  <SendIcon sx={{ fontSize: 24, sm: 28 }} />
                  Submit Help Ticket
                </>
              )}
            </motion.button>
          </form>
        </div>
      </div>
    </div>
  );
}
